import React, {useState, useCallback, useEffect, useRef, useContext} from 'react';
import {Button, setOptions} from "@mobiscroll/react";
import '@mobiscroll/react/dist/css/mobiscroll.min.css';
import '../styles/dashboardWorkspaceApp.css';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faDisplayCode,
    faArrowRotateRight,
    faXmark,
    faCirclePlay,
    faCircleStop,
    faPipe,
    faSquareTerminal,
    faQrcode
} from "@fortawesome/pro-duotone-svg-icons";
import Empty from "../utils/Empty";
import QR from "../utils/QR";
import {NetworkContext} from "../utils/NetworkContext";

setOptions({
    theme: 'ios',
    themeVariant: 'light',
});

function DashboardWorkspaceApp({locale, workspaceItem, ButtonBack, teamtalk, width, setWorkspaceItem}) {
    const {wsCall, wsResponse, setWsResponse, restCall} = useContext(NetworkContext);
    const canvas = useRef(null);
    const svg = useRef(null);
    const container = useRef(null);
    const controls = useRef(null);
    const consoleLog = useRef(null);
    const animationFrame = useRef(null);
    const saveTimeout = useRef(null);
    const storageRef = useRef(workspaceItem.data.storage ? workspaceItem.data.storage : {});
    const intervals = useRef([]);
    const timeouts = useRef([]);
    const [iconHover, setIconHover] = useState(null);
    const [code, setCode] = useState(workspaceItem.data.code);
    const [type, setType] = useState(workspaceItem.subtitle);
    const [qr, setQr] = useState(null);
    const [dialog, setDialog] = useState({});
    const [running, setRunning] = useState(false);
    const [consoleOutput, setConsoleOutput] = useState([]);
    const [showConsole, setShowConsole] = useState(false);
    const [canvasReady, setCanvasReady] = useState(false);

    const sandboxSetInterval = useCallback((fn, delay, ...args) => {
        const id = setInterval(fn, delay, ...args);
        intervals.current.push(id);
        return id;
    }, []);

    const sandboxClearInterval = useCallback((id) => {
        clearInterval(id);
        intervals.current = intervals.current.filter(i => i !== id);
    }, []);

    const sandboxSetTimeout = useCallback((fn, delay, ...args) => {
        const id = setTimeout(fn, delay, ...args);
        timeouts.current.push(id);
        return id;
    }, []);

    const sandboxClearTimeout = useCallback((id) => {
        clearTimeout(id);
        timeouts.current = timeouts.current.filter(i => i !== id);
    }, []);

    const SimShare = () => {
        if (workspaceItem.data.shared) {
            const url = "https://ai.cubicstone.com/?a=" + workspaceItem.data.shared;

            setQr({
                url: url,
                title: null
            })
            setDialog({view: 'utilsQr'});
        } else {

            const message = {
                type: 'wss',
                path: 'shortener-app',
                action: 'put',
                data: {
                    path: 'app',
                    type: 'qr',
                    app: workspaceItem.id,
                    teamtalk:  (!Empty(teamtalk) && teamtalk.id) ? {id: teamtalk.id} : null
                }
            };
            wsCall(message);
        }
    }

    const SimClose = useCallback(() => {
        if (animationFrame.current) {
            cancelAnimationFrame(animationFrame.current);
            animationFrame.current = null;
        }
        if (saveTimeout.current) {
            clearTimeout(saveTimeout.current);
            saveTimeout.current = null;
        }
        setRunning(false);

        ButtonBack();
    }, [ButtonBack]);

    const SimSave = useCallback(() => {

        if (workspaceItem.public) {

            const message = {
                type: 'rest',
                path: 'app',
                action: 'update',
                data: {
                    id: workspaceItem.id,
                    owner: workspaceItem.owner,
                    account: workspaceItem.account,
                    storage: storageRef.current
                }
            };
            restCall(message);
        } else if (!workspaceItem.public) {

            const message = {
                type: 'wss',
                path: 'app',
                action: 'update',
                data: {
                    id: workspaceItem.id,
                    storage: storageRef.current,
                    teamtalk:  (!Empty(teamtalk) && teamtalk.id) ? {id: teamtalk.id, users: teamtalk.users} : null
                }
            };
            wsCall(message);
        }
    }, [workspaceItem.id, workspaceItem.public, teamtalk, wsCall, restCall]);

    const debouncedSave = useCallback(() => {
        if (saveTimeout.current) {
            clearTimeout(saveTimeout.current);
        }

        saveTimeout.current = setTimeout(() => {
            SimSave();
        }, 1000);
    }, [SimSave]);

    const AppendToConsole = useCallback((message, type = 'log') => {
        setConsoleOutput(prev => [...prev, {
            message,
            type,
            timestamp: new Date().toLocaleTimeString()
        }]);
    }, []);

    const ClearConsole = useCallback(() => {
        setConsoleOutput([]);
    }, []);

    const StopExecution = useCallback(() => {
        if (animationFrame.current) {
            cancelAnimationFrame(animationFrame.current);
            animationFrame.current = null;
        }

        if (intervals.current) {
            intervals.current.forEach(id => clearInterval(id));
            intervals.current = [];
        }

        if (timeouts.current) {
            timeouts.current.forEach(id => clearTimeout(id));
            timeouts.current = [];
        }

        setRunning(false);
        AppendToConsole('Execution stopped', 'info');
    }, [AppendToConsole]);

    const StartExecution = useCallback(() => {
        try {
            ClearConsole();
            AppendToConsole('Starting execution...', 'info');

            const safeConsole = {
                log: (...args) => AppendToConsole(args.join(' '), 'log'),
                error: (...args) => AppendToConsole(args.join(' '), 'error'),
                warn: (...args) => AppendToConsole(args.join(' '), 'warn'),
                info: (...args) => AppendToConsole(args.join(' '), 'info')
            };

            const SafeRequestAnimationFrame = (callback) => {
                animationFrame.current = requestAnimationFrame(callback);
                return animationFrame.current;
            };

            const SafeCancelAnimationFrame = (id) => {
                cancelAnimationFrame(id);
                if (animationFrame.current === id) {
                    animationFrame.current = null;
                }
            };

            const SafeSetInterval = (fn, delay, ...args) => {
                const id = setInterval(fn, delay, ...args);
                intervals.current.push(id);
                return id;
            };

            const SafeClearInterval = (id) => {
                clearInterval(id);
                intervals.current = intervals.current.filter(i => i !== id);
            };

            const SafeSetTimeout = (fn, delay, ...args) => {
                const id = setTimeout(fn, delay, ...args);
                timeouts.current.push(id);
                return id;
            };

            const SafeClearTimeout = (id) => {
                clearTimeout(id);
                timeouts.current = timeouts.current.filter(i => i !== id);
            };

            let context = {
                console: safeConsole,
                Math,
                requestAnimationFrame: SafeRequestAnimationFrame,
                cancelAnimationFrame: SafeCancelAnimationFrame,
                setInterval: SafeSetInterval,
                clearInterval: SafeClearInterval,
                setTimeout: SafeSetTimeout,
                clearTimeout: SafeClearTimeout
            };

            if (type === '2D') {
                const canvasElement = canvas.current;
                const ctx = canvasElement.getContext('2d');
                const containerElement = controls.current;

                AppendToConsole(`Canvas: ${canvasElement.width}x${canvasElement.height}`, 'info');

                context = {
                    ...context,
                    canvas: canvasElement,
                    ctx,
                    container: containerElement
                };
            } else if (type === '3D') {
                const canvasElement = canvas.current;
                AppendToConsole(`Canvas dimensions: ${canvasElement.width}x${canvasElement.height}`, 'info');

                let gl = canvasElement.getContext('webgl', {
                    alpha: true,
                    depth: true,
                    stencil: false,
                    antialias: true,
                    premultipliedAlpha: true,
                    preserveDrawingBuffer: false
                });

                if (!gl) {
                    gl = canvasElement.getContext('webgl2');
                }

                if (!gl) {
                    gl = canvasElement.getContext('experimental-webgl');
                }

                if (!gl) {
                    AppendToConsole('WebGL not supported', 'error');
                    AppendToConsole('Check chrome://gpu for details', 'error');
                    return;
                }

                AppendToConsole(`WebGL context: ${gl.constructor.name}`, 'info');

                gl.viewport(0, 0, canvasElement.width, canvasElement.height);

                const viewport = gl.getParameter(gl.VIEWPORT);
                AppendToConsole(`Viewport: [${viewport[0]}, ${viewport[1]}, ${viewport[2]}, ${viewport[3]}]`, 'info');

                context = {
                    ...context,
                    canvas: canvasElement,
                    gl
                };
            } else if (type === 'SVG') {
                const svgElement = svg.current;
                const NS = 'http://www.w3.org/2000/svg';

                context = {
                    ...context,
                    svg: svgElement,
                    NS
                };
            } else if (type === 'HTML') {
                const containerElement = container.current;

                const scopedDocument = {
                    createElement: document.createElement.bind(document),
                    createElementNS: document.createElementNS.bind(document),
                    createTextNode: document.createTextNode.bind(document),
                    createDocumentFragment: document.createDocumentFragment.bind(document),

                    getElementById: (id) => containerElement.querySelector(`#${id}`),
                    querySelector: (selector) => containerElement.querySelector(selector),
                    querySelectorAll: (selector) => containerElement.querySelectorAll(selector),
                    getElementsByClassName: (className) => containerElement.getElementsByClassName(className),
                    getElementsByTagName: (tagName) => containerElement.getElementsByTagName(tagName),

                    addEventListener: containerElement.addEventListener.bind(containerElement),
                    removeEventListener: containerElement.removeEventListener.bind(containerElement),

                    get body() {
                        return containerElement;
                    }
                };

                const storageAPI = {
                    get: (key) => {
                        return storageRef.current[key];
                    },

                    set: (key, value) => {
                        storageRef.current = { ...storageRef.current, [key]: value };
                        debouncedSave();
                    },

                    getAll: () => {
                        return { ...storageRef.current };
                    },

                    setAll: (data) => {
                        storageRef.current = data;
                        debouncedSave();
                    },

                    remove: (key) => {
                        const updated = { ...storageRef.current };
                        delete updated[key];
                        storageRef.current = updated;
                        debouncedSave();
                    },

                    clear: () => {
                        storageRef.current = {};
                        debouncedSave();
                    },

                    has: (key) => {
                        return key in storageRef.current;
                    },

                    keys: () => {
                        return Object.keys(storageRef.current);
                    },

                    get length() {
                        return Object.keys(storageRef.current).length;
                    }
                };

                context = {
                    ...context,
                    container: containerElement,
                    document: scopedDocument,
                    storage: storageAPI
                };
            }

            const executeFunction = new Function(
                ...Object.keys(context),
                code
            );

            executeFunction(...Object.values(context));

            setRunning(true);
            AppendToConsole('Execution started successfully', 'info');

        } catch (error) {
            AppendToConsole(`Error: ${error.message}`, 'error');
            console.error('Execution error:', error);
            setRunning(false);
        }
    }, [code, type, ClearConsole, AppendToConsole, sandboxSetInterval, sandboxClearInterval, sandboxSetTimeout, sandboxClearTimeout]);

    const ResetExecution = useCallback(() => {
        StopExecution();
        ClearConsole();

        if (type === '2D' && canvas.current) {
            const ctx = canvas.current.getContext('2d');
            ctx.clearRect(0, 0, canvas.current.width, canvas.current.height);
        }

        if (type === '3D' && canvas.current) {
            const gl = canvas.current.getContext('webgl');
            if (gl) {
                gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
            }
        }

        if (type === 'SVG' && svg.current) {
            while (svg.current.firstChild) {
                svg.current.removeChild(svg.current.firstChild);
            }
        }

        if (type === 'HTML' && container.current) {
            container.current.innerHTML = '';
        }

        if (controls.current) {
            controls.current.innerHTML = '';
        }

        AppendToConsole('Restarting...', 'info');
        StartExecution();
    }, [type, StopExecution, ClearConsole, AppendToConsole, StartExecution]);

    const PutShortenerApp = useCallback(data => {
        if (data.shared) {
            const item = workspaceItem;
            item.data.shared = data.shared;
            setWorkspaceItem(item);
        }
        if (data.qr) {
            setQr(data.qr);
            setDialog({view: 'utilsQr'});
        }
    }, [workspaceItem]);

    useEffect(() => {
        if (wsResponse && !Empty(wsResponse) && wsResponse.action === 'put' && wsResponse.path === 'shortener-app') {
            PutShortenerApp(wsResponse.data);
            setWsResponse({});
        }
    }, [wsResponse, PutShortenerApp, setWsResponse]);

    useEffect(() => {
        if (!canvasReady) return;

        const timer = setTimeout(() => {
            StartExecution();
        }, 150);

        return () => {
            clearTimeout(timer);
            StopExecution();
        };
    }, [canvasReady, StartExecution, StopExecution]);

    useEffect(() => {
        if ((type === '2D' || type === '3D') && canvas.current) {
            const UpdateCanvasSize = () => {
                const containerElement = canvas.current?.parentElement;
                if (containerElement) {
                    const width = containerElement.clientWidth - 40;
                    const height = containerElement.clientHeight - 40;

                    canvas.current.width = width;
                    canvas.current.height = height;

                    canvas.current.style.width = width + 'px';
                    canvas.current.style.height = height + 'px';

                    console.log('Canvas sized:', width, 'x', height);

                    setCanvasReady(true);
                }
            };

            setTimeout(() => {
                UpdateCanvasSize();
            }, 50);

            window.addEventListener('resize', UpdateCanvasSize);

            return () => window.removeEventListener('resize', UpdateCanvasSize);
        } else {
            setCanvasReady(true);
        }
    }, [type]);

    useEffect(() => {
        return () => {
            if (saveTimeout.current) {
                clearTimeout(saveTimeout.current);
            }
        };
    }, []);

    return (
        <div className="cs-dashboard-workspace-app-fullscreen">
            <div className="mbsc-row cs-dashboard-workspace-app-header" >
                <div className="mbsc-col cs-dashboard-workspace-app-col-start" >
                    {((width > 576 && !workspaceItem.public) || workspaceItem.public) &&
                        <Button color="secondary" variant="flat"  className="mbsc-bold cs-dashboard-workspace-app-color" >
                            <FontAwesomeIcon className="cs-dashboard-workspace-app-icon" icon={faDisplayCode} />
                            {((width > 992 && !workspaceItem.public) || (width > 576 && workspaceItem.public)) &&
                                <div className="cs-dashboard-workspace-app-menu-text">
                                    {workspaceItem.name}
                                </div>
                            }
                            {width > 992 && !workspaceItem.public &&
                                <>
                                    <FontAwesomeIcon className="cs-dashboard-workspace-app-icon-pipe" icon={faPipe} />
                                    <div className="cs-dashboard-workspace-app-menu-text">
                                        {workspaceItem.subtitle}
                                    </div>
                                </>
                            }
                            {width < 992 && !workspaceItem.public &&
                                <>&nbsp;</>
                            }
                        </Button>
                    }
                </div>
                <div className="mbsc-col-auto" >
                    <div className="mbsc-row cs-dashboard-workspace-app-col-end">
                        <Button onMouseEnter={() => setIconHover('share')} onMouseLeave={() => setIconHover(null)} color="primary" variant="flat" className="mbsc-bold cs-dashboard-workspace-app-color" onClick={() => SimShare()} >
                            {width > 576 && workspaceItem.public && <>&nbsp;</>}<FontAwesomeIcon className="cs-dashboard-workspace-app-icon" icon={faQrcode} beatFade={iconHover === 'share'} />{width > 768 && !workspaceItem.public && <div className="cs-dashboard-workspace-app-menu-text">{locale.dashboardWorkspaceApp.h}</div>}
                        </Button>
                        {!workspaceItem.public &&
                            <>
                                <Button onMouseEnter={() => setIconHover('console')} onMouseLeave={() => setIconHover(null)} color="primary" variant="flat" className="mbsc-bold cs-dashboard-workspace-app-color" onClick={() => setShowConsole(!showConsole)} >
                                    <FontAwesomeIcon className="cs-dashboard-workspace-app-icon" icon={faSquareTerminal} beatFade={iconHover === 'console'} />{width > 768 && <div className="cs-dashboard-workspace-app-menu-text">{locale.dashboardWorkspaceApp.d}</div>}
                                </Button>
                                {type !== 'HTML' &&
                                    <Button onMouseEnter={() => setIconHover('run')} onMouseLeave={() => setIconHover(null)} color="primary" variant="flat" className="mbsc-bold cs-dashboard-workspace-app-color" onClick={() => running ? StopExecution() : StartExecution()} >
                                        <FontAwesomeIcon className="cs-dashboard-workspace-app-icon" icon={running ? faCircleStop : faCirclePlay} beatFade={iconHover === 'run'} />{width > 576 && <div className="cs-dashboard-workspace-app-menu-text">{running ? locale.dashboardWorkspaceApp.b : locale.dashboardWorkspaceApp.c}</div>}
                                    </Button>
                                }
                                <Button onMouseEnter={() => setIconHover('reset')} onMouseLeave={() => setIconHover(null)} color="primary" variant="flat" className="mbsc-bold cs-dashboard-workspace-app-color" onClick={() => ResetExecution()} >
                                    <FontAwesomeIcon className="cs-dashboard-workspace-app-icon" icon={faArrowRotateRight} spin={iconHover === 'reset'} />{width > 375 && <div className="cs-dashboard-workspace-app-menu-text">{locale.dashboardWorkspaceApp.a}</div>}
                                </Button>
                                <Button onMouseEnter={() => setIconHover('close')} onMouseLeave={() => setIconHover(null)} color="primary" variant="flat" className="mbsc-bold cs-dashboard-workspace-app-color" onClick={() => SimClose()} >
                                    <FontAwesomeIcon className="cs-dashboard-workspace-app-icon" icon={faXmark} fade={iconHover === 'close'} />
                                </Button>
                            </>
                        }
                    </div>
                </div>
            </div>
            <div className="cs-dashboard-workspace-app-content">
                <div className="cs-dashboard-workspace-app-render-area">
                    {(type === '2D' || type === '3D') && (
                        <canvas
                            ref={canvas}
                            className="cs-dashboard-workspace-app-canvas"
                        />
                    )}
                    {type === 'SVG' && (
                        <svg
                            ref={svg}
                            className="cs-dashboard-workspace-app-svg"
                            width="100%"
                            height="100%"
                        />
                    )}
                    {type === 'HTML' && (
                        <div
                            ref={container}
                            className="cs-dashboard-workspace-app-html-container"
                        />
                    )}
                </div>
                <div
                    ref={controls}
                    className="cs-dashboard-workspace-app-controls"
                />
            </div>
            {!workspaceItem.public && showConsole &&
                <div className="cs-dashboard-workspace-app-console cs-dashboard-workspace-app-console-open">
                    <div className="cs-dashboard-workspace-app-console-header">
                        <span>{locale.dashboardWorkspaceApp.e}</span>
                        <div className="cs-dashboard-workspace-app-console-actions">
                            <button
                                className="cs-dashboard-workspace-app-console-btn"
                                onClick={ClearConsole}
                            >
                                {locale.dashboardWorkspaceApp.f}
                            </button>
                            <button
                                className="cs-dashboard-workspace-app-console-btn"
                                onClick={() => setShowConsole(!showConsole)}
                            >
                                ▼ {locale.dashboardWorkspaceApp.g}
                            </button>
                        </div>
                    </div>
                    <div
                        ref={consoleLog}
                        className="cs-dashboard-workspace-app-console-content"
                    >
                        {consoleOutput.length === 0 ? (
                            <div className="cs-dashboard-workspace-app-console-empty">No output</div>
                        ) : (
                            consoleOutput.map((entry, index) => (
                                <div
                                    key={index}
                                    className={`cs-dashboard-workspace-app-console-entry cs-dashboard-workspace-app-console-${entry.type}`}
                                >
                                    <span className="cs-dashboard-workspace-app-console-timestamp">{entry.timestamp}</span>
                                    <span className="cs-dashboard-workspace-app-console-message">{entry.message}</span>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            }
            {dialog.view === 'utilsQr' && !Empty(qr) &&
                <QR
                    setQr={setQr}
                    dialog={dialog}
                    setDialog={setDialog}
                    qr={qr}
                    width={width}
                    locale={!Empty(locale) ? locale : {}}
                />
            }
        </div>
    );
}

export default DashboardWorkspaceApp;
